// ─────────────────────────────────────────────────────────────
// ManageStrategies
// Extracted from App.js.
// ─────────────────────────────────────────────────────────────

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Btn } from "../../components/ui/Btn";
import { C } from "../../lib/colors";

export function ManageStrategies({
  customStrategies,
  addCustomStrategy,
  deleteCustomStrategy,
  setView,
  strategyPreferences,
  setStrategyPreferences,
  strategyPrefsLoading = false,
}) {
  const navigate = useNavigate();
  const [newStrategyName, setNewStrategyName] = useState("");
  const [saving, setSaving] = useState(false);
  
  const builtInList = [
    { value: "Breakout_NewHigh", label: "Breakout - New High/Low" },
    { value: "Pullback_Support", label: "Pullback to Support/Resistance" },
    { value: "Reversal_Candle", label: "Reversal Candle (Pinbar, Engulfing)" },
    { value: "InsideBar_Breakout", label: "Inside Bar Breakout" },
    { value: "Flag_Pennant", label: "Bull/Bear Flag or Pennant" },
    { value: "Trend_Continuation", label: "Trend Continuation" },
    { value: "MA_Crossover", label: "Moving Average Crossover" },
    { value: "EMA_Ribbon", label: "EMA Ribbon Alignment" },
    { value: "Volume_Spike", label: "Volume Spike + Price Action" },
    { value: "RSI_Divergence", label: "RSI Divergence" },
    { value: "Bollinger_Squeeze", label: "Bollinger Band Squeeze" },
    { value: "VWAP_Reclaim", label: "VWAP Reclaim" },
    { value: "Scalp_Momentum", label: "Scalp - Momentum Ignition" },
    { value: "Opening_Range_Breakout", label: "Opening Range Breakout (ORB)" },
    { value: "News_Catalyst", label: "News / Economic Catalyst" },
    { value: "Confluence_Multiple", label: "Multiple Indicator Confluence" },
    { value: "OrderFlow_Delta", label: "Order Flow / Delta Divergence" },
    { value: "Mean_Reversion", label: "Mean Reversion Setup" },
  ];

  const isEnabled = (value) => {
    return strategyPreferences.builtInEnabled[value] !== false;
  };

  const toggleBuiltIn = async (value) => {
    const newPrefs = {
      ...strategyPreferences.builtInEnabled,
      [value]: !isEnabled(value)
    };
    setSaving(true);
    await setStrategyPreferences({ builtInEnabled: newPrefs });
    setSaving(false);
  };

  const handleAddCustom = () => {
    if (newStrategyName.trim()) {
      addCustomStrategy({
        name: newStrategyName.trim(),
        enabled: true
      });
      setNewStrategyName("");
    }
  };

  return (
    <div style={{ maxWidth: 720, margin: "0 auto", padding: 20 }}>
      <button onClick={() => setView("dashboard")} style={{ color: C.muted }}>← Back</button>
      <div style={{ display: "flex", alignItems: "baseline", gap: 12 }}>
        <h2 style={{ margin: 0 }}>Manage Strategies</h2>
        {(strategyPrefsLoading || saving) && (
          <span style={{ fontSize: 11, color: C.muted }}>
            {strategyPrefsLoading ? "Loading…" : "Saving…"}
          </span>
        )}
      </div>
      <p style={{color: C.muted}}>Toggle which strategies appear in dropdowns.</p>

      <h3>Built-in Strategies</h3>
      <div style={{display: "grid", gap: 12, marginBottom: 40}}>
        {builtInList.map(s => (
          <div key={s.value} style={{display:"flex", justifyContent:"space-between", alignItems:"center", padding:"14px", background:"#1a1d2e", borderRadius:8}}>
            <span>{s.label}</span>
            <input
              type="checkbox"
              checked={isEnabled(s.value)}
              onChange={() => toggleBuiltIn(s.value)}
            />
          </div>
        ))}
      </div>

      <h3>Your Custom Strategies</h3>
      <div className="fos-btn-row" style={{display:"flex", gap:12, marginBottom:20}}>
        <input
          type="text"
          value={newStrategyName}
          onChange={e => setNewStrategyName(e.target.value)}
          placeholder="New custom strategy name"
          style={{flex:1, padding:14, background:"#1a1d2e", border:`1px solid ${C.border}`, borderRadius:8, color:C.text}}
          onKeyDown={e => e.key === 'Enter' && handleAddCustom()}
        />
        <Btn onClick={handleAddCustom}>Add</Btn>
      </div>

      {customStrategies.length > 0 && customStrategies.map(strat => (
        <div key={strat.id} style={{padding:"14px", background:"#1a1d2e", borderRadius:8, marginBottom:8, display:"flex", justifyContent:"space-between"}}>
          <span>{strat.name}</span>
          <button onClick={() => deleteCustomStrategy(strat.id)} style={{color: C.red}}>Delete</button>
        </div>
      ))}

      {/* Pointer to Trading Rules — formerly the Risk Limits section.
          Drawdown / trade caps / blackouts now live in the per-account
          rules system. This card just points users there. */}
      <div style={{
        marginTop: 16,
        marginBottom: 24,
        padding: 16,
        background: "#0f111a",
        border: `1px solid ${C.border}`,
        borderRadius: 12,
        display: "flex",
        alignItems: "center",
        gap: 14,
      }}>
        <div style={{ fontSize: 28 }}>🛡️</div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>
            Risk Limits & Trading Rules
          </div>
          <div style={{ fontSize: 11, color: C.muted, marginTop: 3, lineHeight: 1.5 }}>
            Daily drawdown, trade caps, blackout windows, and news avoidance are
            now configured per account in Trading Rules.
          </div>
        </div>
        <button
          onClick={() => navigate("/settings/rules")}
          style={{
            padding: "8px 14px",
            background: C.blue + "20",
            border: `1px solid ${C.blue}50`,
            borderRadius: 6,
            color: C.blue,
            cursor: "pointer",
            fontSize: 12,
            fontWeight: 600,
            whiteSpace: "nowrap",
          }}
        >
          Open Rules →
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// CALENDAR HOOK — ForexFactory public JSON feed
// ─────────────────────────────────────────────────────────────
